import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import { useAbTestingSdk } from '@sravni/react-utils';

import { useSelectUser } from '@src/reducers/user';
import { isServer } from '@src/utils';
import { isWhiteLabelPage } from '@src/utils/routing';

import { useGetWlPageAffClickId } from './hooks';
import type { IMakePixelProps } from './makePixelLink';
import { makePixelLink } from './makePixelLink';

export const usePixel = (link?: string, props?: Partial<IMakePixelProps>) => {
  const user = useSelectUser();
  const { asPath, pathname, query } = useRouter();

  const isWhiteLabel = isWhiteLabelPage(asPath);

  const affClickIdForWhiteLabel = useGetWlPageAffClickId();

  const abTestingSdk = useAbTestingSdk();

  const pixelLink = useMemo(() => {
    if (!link || isServer) {
      return null;
    }

    return makePixelLink(link, {
      ...props,
      pathname: asPath,
      userId: user?.sub,
      isWL: isWhiteLabel,
      route: pathname,
      abTestingSdkExperiments: abTestingSdk.getAllUserExperiments(),
      affClickId: affClickIdForWhiteLabel,
      query,
    });
  }, [abTestingSdk, asPath, isWhiteLabel, link, pathname, props, query, user?.sub, affClickIdForWhiteLabel]);

  const createPixelLink = useCallback(
    (href?: string) =>
      makePixelLink(href || link, {
        ...props,
        pathname: asPath,
        userId: user?.sub,
        isWL: isWhiteLabel,
        route: pathname,
        abTestingSdkExperiments: abTestingSdk.getAllUserExperiments(),
        affClickId: affClickIdForWhiteLabel,
        query,
      }),
    [abTestingSdk, asPath, isWhiteLabel, link, pathname, props, query, user?.sub, affClickIdForWhiteLabel],
  );

  return { pixelLink, createPixelLink };
};
